/**
 * TanStack Query hooks for resource and stack data.
 *
 * All fetch functions throw on non-OK responses so TanStack Query's error
 * state is populated (Pattern 8.2 / NFR-RESIL-FE-02).
 *
 * Query keys:
 *   ['stacks', filter]   — stack view grouped by application + environment
 *   ['resources', filter] — flat resource grid
 *   ['status']           — dashboard summary counts + stale detection
 */
import { useQuery } from '@tanstack/react-query';
import type { ApplicationStack, ResourceFilter, StackFilter, DashboardStatus } from '@/types';

// ── Fetch helpers ─────────────────────────────────────────────────────────────

async function fetchStacks(filter: StackFilter): Promise<ApplicationStack[]> {
  const params = new URLSearchParams();
  if (filter.application) params.set('application', filter.application);
  if (filter.environment) params.set('environment', filter.environment);

  const url = `/api/stacks${params.size > 0 ? `?${params}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to load stacks: ${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<ApplicationStack[]>;
}

async function fetchResources(filter: ResourceFilter): Promise<unknown[]> {
  const params = new URLSearchParams();
  if (filter.application) params.set('application', filter.application);
  if (filter.environment) params.set('environment', filter.environment);
  if (filter.type) params.set('type', filter.type);

  const url = `/api/resources${params.size > 0 ? `?${params}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to load resources: ${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<unknown[]>;
}

async function fetchDashboardStatus(): Promise<DashboardStatus> {
  const res = await fetch('/api/dashboard/status');
  if (!res.ok) {
    throw new Error(`Failed to load dashboard status: ${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<DashboardStatus>;
}

// ── Query hooks ───────────────────────────────────────────────────────────────

/**
 * Fetches application stacks, optionally filtered by application and/or environment.
 * Refetches on the given interval (controlled by usePollingInterval).
 */
export function useStacks(filter: StackFilter, refetchInterval: number) {
  return useQuery({
    queryKey: ['stacks', filter],
    queryFn: () => fetchStacks(filter),
    refetchInterval,
  });
}

/**
 * Fetches a flat list of all resources for the resource grid view.
 * Accepts a ResourceFilter (application, environment, type).
 */
export function useResources(filter: ResourceFilter, refetchInterval: number) {
  return useQuery({
    queryKey: ['resources', filter],
    queryFn: () => fetchResources(filter),
    refetchInterval,
  });
}

/**
 * Fetches the dashboard summary (counts, last-collection timestamps, stale accounts).
 * Refetches on the given interval.
 */
export function useDashboardStatus(refetchInterval: number) {
  return useQuery({
    queryKey: ['status'],
    queryFn: fetchDashboardStatus,
    refetchInterval,
  });
}
